import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';

export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;
export const SCALE_STEP = 1.2;

export interface C4Transform {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: C4Transform = { scale: 1, x: 0, y: 0 };

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function zoomAt(
  transform: C4Transform,
  factor: number,
  originX: number,
  originY: number,
): C4Transform {
  const scale = clampScale(transform.scale * factor);
  const applied = scale / transform.scale;
  return {
    scale,
    x: originX - (originX - transform.x) * applied,
    y: originY - (originY - transform.y) * applied,
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    touchAction: 'none',
  },
  viewport: {
    overflow: 'hidden',
    cursor: 'grab',
  },
  panning: {
    cursor: 'grabbing',
  },
  content: {
    transformOrigin: '0 0',
    width: '100%',
  },
  controls: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  level: {
    minWidth: 44,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
  },
}));

export interface C4ZoomableCanvasProps {
  children: ReactNode;
  height?: number | string;
  className?: string;
}

export function C4ZoomableCanvas({
  children,
  height = 520,
  className,
}: C4ZoomableCanvasProps) {
  const classes = useStyles();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const panOrigin = useRef<{ x: number; y: number } | null>(null);
  const [transform, setTransform] = useState<C4Transform>(IDENTITY);
  const [panning, setPanning] = useState(false);

  const zoomByStep = useCallback((factor: number) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const originX = rect ? rect.width / 2 : 0;
    const originY = rect ? rect.height / 2 : 0;
    setTransform(current => zoomAt(current, factor, originX, originY));
  }, []);

  const reset = useCallback(() => setTransform(IDENTITY), []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const factor = event.deltaY < 0 ? SCALE_STEP : 1 / SCALE_STEP;
      setTransform(current =>
        zoomAt(
          current,
          factor,
          event.clientX - rect.left,
          event.clientY - rect.top,
        ),
      );
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button > 0) {
        return;
      }
      panOrigin.current = {
        x: event.clientX - transform.x,
        y: event.clientY - transform.y,
      };
      setPanning(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [transform.x, transform.y],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const origin = panOrigin.current;
      if (!origin) {
        return;
      }
      setTransform(current => ({
        ...current,
        x: event.clientX - origin.x,
        y: event.clientY - origin.y,
      }));
    },
    [],
  );

  const endPan = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    panOrigin.current = null;
    setPanning(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  return (
    <Box
      className={className ? `${classes.root} ${className}` : classes.root}
      data-testid="c4-zoomable-canvas"
    >
      <div
        ref={viewportRef}
        className={
          panning ? `${classes.viewport} ${classes.panning}` : classes.viewport
        }
        style={{ height }}
        data-testid="c4-zoom-viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerLeave={endPan}
        onPointerCancel={endPan}
      >
        <Box
          className={classes.content}
          data-testid="c4-zoom-content"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          }}
        >
          {children}
        </Box>
      </div>

      <Box className={classes.controls}>
        <Tooltip title="Reduzir">
          <span>
            <IconButton
              size="small"
              aria-label="Reduzir zoom"
              disabled={transform.scale <= MIN_SCALE}
              onClick={() => zoomByStep(1 / SCALE_STEP)}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.level}
          data-testid="c4-zoom-level"
        >
          {`${Math.round(transform.scale * 100)}%`}
        </Typography>
        <Tooltip title="Ampliar">
          <span>
            <IconButton
              size="small"
              aria-label="Ampliar zoom"
              disabled={transform.scale >= MAX_SCALE}
              onClick={() => zoomByStep(SCALE_STEP)}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Restaurar zoom">
          <IconButton
            size="small"
            aria-label="Restaurar zoom"
            onClick={reset}
            data-testid="c4-zoom-reset"
          >
            <CenterFocusStrongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
